import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';

// ── Types ─────────────────────────────────────────────────────────────────────

type Step = 'credentials' | 'totp' | 'totp_setup' | 'totp_setup_codes';

interface TokenPair {
  access_token: string;
  refresh_token: string;
}

interface TotpChallenge {
  totp_required: true;
  temp_token: string;
}

interface TotpSetupRequired {
  totp_setup_required: true;
  setup_token: string;
}

interface TotpSetupInfo {
  otpauth_uri: string;
  secret: string;
}

interface TotpEnableResult {
  recovery_codes: string[];
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function LoginPage(): React.ReactElement {
  const [step, setStep] = useState<Step>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupInfo, setSetupInfo] = useState<TotpSetupInfo | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const tempTokenRef = useRef<string>('');
  const setupTokenRef = useRef<string>('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  // ── Étape 1 : email + password ───────────────────────────────────────────────

  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';

  async function handleCredentials(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // OAuth2PasswordRequestForm exige application/x-www-form-urlencoded
      const body = new URLSearchParams({ username, password });
      const res = await fetch(`${apiBase}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!res.ok) {
        throw new Error('Identifiants invalides');
      }

      const data = await res.json() as TokenPair | TotpChallenge | TotpSetupRequired;

      if ('totp_required' in data && data.totp_required) {
        // 2FA requis — passer à l'étape TOTP
        tempTokenRef.current = data.temp_token;
        setStep('totp');
        return;
      }

      if ('totp_setup_required' in data && data.totp_setup_required) {
        // TOTP obligatoire non configuré (clearance C4) — lancer le setup
        setupTokenRef.current = data.setup_token;
        const setupRes = await fetch(`${apiBase}/api/v1/user/totp/setup`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${data.setup_token}` },
        });
        if (!setupRes.ok) throw new Error("Impossible d'initialiser le TOTP");
        const info = await setupRes.json() as TotpSetupInfo;
        setSetupInfo(info);
        setTotpCode('');
        setStep('totp_setup');
        return;
      }

      // Pas de TOTP — connexion directe
      const pair = data as TokenPair;
      login(pair.access_token, pair.refresh_token);
      navigate(from, { replace: true });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }

  // ── Étape 2 : code TOTP ───────────────────────────────────────────────────────

  async function handleTotpVerify(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${apiBase}/api/v1/auth/totp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempTokenRef.current, code: totpCode }),
      });

      if (!res.ok) {
        const detail = await res.json().catch(() => ({})) as { detail?: string };
        throw new Error(detail.detail ?? 'Code invalide ou expiré');
      }

      const pair = await res.json() as TokenPair;
      login(pair.access_token, pair.refresh_token);
      navigate(from, { replace: true });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de vérification');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  }

  // ── Étape 3 : activer le TOTP après scan du QR ───────────────────────────────

  async function handleTotpSetupEnable(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/v1/user/totp/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${setupTokenRef.current}`,
        },
        body: JSON.stringify({ code: totpCode }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({})) as { detail?: string };
        throw new Error(detail.detail ?? 'Code invalide — vérifiez votre application');
      }
      const result = await res.json() as TotpEnableResult;
      setRecoveryCodes(result.recovery_codes);
      setStep('totp_setup_codes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur d\'activation');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  }

  // ── Code de récupération ──────────────────────────────────────────────────────

  async function handleRecovery(): Promise<void> {
    const code = window.prompt('Code de récupération (format XXXX-XXXX-XXXX-XXXX) :');
    if (!code) return;
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${apiBase}/api/v1/auth/totp/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempTokenRef.current, recovery_code: code }),
      });

      if (!res.ok) {
        const detail = await res.json().catch(() => ({})) as { detail?: string };
        throw new Error(detail.detail ?? 'Code de récupération invalide');
      }

      const pair = await res.json() as TokenPair;
      login(pair.access_token, pair.refresh_token);
      navigate(from, { replace: true });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de récupération');
    } finally {
      setLoading(false);
    }
  }

  // ── Rendu ──────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm p-8 space-y-4 bg-card rounded-lg border border-border shadow-sm">

        <h1 className="text-2xl font-bold text-foreground">Assistia Admin</h1>

        {/* ── Étape 3 : Setup TOTP (QR + premier code) ── */}
        {step === 'totp_setup' && setupInfo && (
          <>
            <p className="text-sm text-muted-foreground">
              Votre compte requiert la double authentification. Scannez ce QR code
              avec Google Authenticator ou Authy, puis saisissez le premier code généré.
            </p>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded">
                {error}
              </div>
            )}

            <div className="flex flex-col items-center gap-3">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupInfo.otpauth_uri)}`}
                alt="QR TOTP"
                className="rounded border border-border"
                width={180}
                height={180}
              />
              <p className="text-xs text-muted-foreground text-center">
                Ou saisissez manuellement&nbsp;:
                <span className="block font-mono text-foreground mt-1 tracking-wider">
                  {setupInfo.secret}
                </span>
              </p>
            </div>

            <form onSubmit={handleTotpSetupEnable} className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="totp-setup" className="text-sm font-medium">
                  Premier code (6 chiffres)
                </label>
                <input
                  id="totp-setup"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm text-center tracking-[0.5em] text-lg font-mono"
                  placeholder="000000"
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              <button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Activation…' : 'Activer le 2FA'}
              </button>
            </form>
          </>
        )}

        {/* ── Étape 4 : Codes de récupération ── */}
        {step === 'totp_setup_codes' && (
          <>
            <p className="text-sm font-medium text-foreground">
              🎉 Double authentification activée !
            </p>
            <p className="text-sm text-muted-foreground">
              Conservez ces codes de récupération en lieu sûr. Ils ne s'affichent
              qu'une seule fois et permettent de vous connecter si vous perdez
              l'accès à votre application d'authentification.
            </p>
            <div className="bg-muted rounded-md p-3 space-y-1">
              {recoveryCodes.map((c) => (
                <p key={c} className="font-mono text-sm text-foreground">{c}</p>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setStep('credentials');
                setError('');
                setTotpCode('');
                setSetupInfo(null);
                setRecoveryCodes([]);
              }}
              className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Se connecter
            </button>
          </>
        )}

        {step === 'credentials' ? (
          <>
            <p className="text-sm text-muted-foreground">
              Connexion réservée aux administrateurs.
            </p>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleCredentials} className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="username" className="text-sm font-medium">Identifiant</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                  required
                  autoComplete="username"
                  autoFocus
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="password" className="text-sm font-medium">Mot de passe</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Connexion…' : 'Se connecter'}
              </button>
            </form>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Saisissez le code à 6 chiffres affiché dans votre application
              d'authentification (Google Authenticator, Authy…).
            </p>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleTotpVerify} className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="totp" className="text-sm font-medium">
                  Code de vérification
                </label>
                <input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm text-center tracking-[0.5em] text-lg font-mono"
                  placeholder="000000"
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Vérification…' : 'Vérifier'}
              </button>
            </form>

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => { setStep('credentials'); setError(''); setTotpCode(''); }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Retour
              </button>
              <button
                type="button"
                onClick={handleRecovery}
                disabled={loading}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Utiliser un code de récupération
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
